export type Channel = "sale" | "rental";

export type Dealer = {
  id: string;
  name: string;
  website: string;
  phone: string;
  whatsapp: string;
  email: string;
  city: string;
  province: string;
};

export type Vehicle = {
  id: string;
  dealerId: string;
  make: string;
  model: string;
  year: number;
  km: number;
  fuel: string;
  transmission: string;
  price: number;
  monthly: number;
  status: string;
  body: string;
  image: string;
  images?: string[];
  description?: string;
  details?: Record<string, unknown>;
  channels: Channel[];
  publicUrl: string;
  publishedAt: string;
  dealer: Dealer;
};

export const vehicleDisplayName=(vehicle:Pick<Vehicle,"make"|"model"|"details">)=>
  [vehicle.make,vehicle.model,vehicle.details?.version].map(value=>String(value||"").trim()).filter(Boolean).join(" ");

export function taxRegime(vehicle:Vehicle){return String(vehicle.details?.taxRegime||"REBU")}
export function taxRate(vehicle:Vehicle){const regime=taxRegime(vehicle);return Number(vehicle.details?.taxRate|| (regime==="IVA"?21:regime==="IGIC"?7:0))}
export function netPrice(vehicle:Vehicle){const stored=Number(vehicle.details?.netPrice||0),rate=taxRate(vehicle);return stored>0?stored:rate>0?Math.round(vehicle.price/(1+rate/100)):vehicle.price}
export function taxCaption(vehicle:Vehicle){const regime=taxRegime(vehicle);return regime==="REBU"?"Impuestos incluidos · REBU":`${regime} ${taxRate(vehicle)}% incluido`}

type Feed = { version: string; dealer: Dealer; vehicles: Omit<Vehicle, "dealer">[] };

function absoluteMedia(url: string, website: string) {
  if (!url) return "";
  try {
    const absolute = new URL(url, website);
    if (["gtico.es", "www.gtico.es"].includes(absolute.hostname) &&
        (absolute.pathname.startsWith("/vehicles/") || absolute.pathname.startsWith("/api/images/"))) {
      return `/api/media?url=${encodeURIComponent(absolute.toString())}`;
    }
    return absolute.toString();
  } catch { return url; }
}

export function configuredFeeds() {
  const defaultFeed=process.env.NODE_ENV==="production"?"https://gtico.es/api/catalog-feed":"http://localhost:3000/api/catalog-feed";
  return (process.env.DEALER_FEEDS || defaultFeed)
    .split(",")
    .map(value => value.trim())
    .filter(Boolean);
}

export async function loadCatalog(): Promise<{ vehicles: Vehicle[]; errors: string[] }> {
  const settled = await Promise.allSettled(configuredFeeds().map(async url => {
    const response = await fetch(url, { next: { revalidate: 60 }, signal:AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
    const feed = await response.json() as Feed;
    if (feed.version !== "1.0" || !feed.dealer?.id || !Array.isArray(feed.vehicles)) {
      throw new Error(`${url}: contrato de catálogo no compatible`);
    }
    return feed.vehicles.map(vehicle => ({
      ...vehicle,
      image: absoluteMedia(vehicle.image, feed.dealer.website),
      images: vehicle.images?.map(image => absoluteMedia(image, feed.dealer.website)),
      dealer: feed.dealer,
    }));
  }));

  return {
    vehicles: settled.flatMap(result => result.status === "fulfilled" ? result.value : []),
    errors: settled.flatMap(result => result.status === "rejected" ? [String(result.reason)] : []),
  };
}
